import { QuestionId } from '../data/questions'

export type Answers = Partial<Record<QuestionId, string>>

export type Dimension = {
  key: string
  label: string
  score: number
}

export type Diagnosis = {
  headline: string
  focus: string[]
  explanation: string
  score: number
  skinTypeLabel: string
  strengths: Dimension[]
  improvements: Dimension[]
}

const clamp = (value: number): number => Math.max(15, Math.min(97, Math.round(value)))

const skinTypeLabels: Record<string, string> = {
  seca: 'Seca',
  mixta: 'Mixta',
  grasa: 'Grasa',
  sensible: 'Sensible',
  nose: 'Sin definir',
}

const dimensions = (answers: Answers): Dimension[] => {
  const { skinType, concern, morning, goal, spf, products, age } = answers

  let hydration = 88
  if (skinType === 'seca') hydration -= 22
  if (morning === 'tirante') hydration -= 26
  if (morning === 'variable') hydration -= 10
  if (concern === 'deshidratacion') hydration -= 18
  if (goal === 'hidratacion') hydration -= 8
  if (products === 'ninguno') hydration -= 12

  let barrier = 86
  if (skinType === 'sensible') barrier -= 26
  if (concern === 'rojez') barrier -= 22
  if (goal === 'calmar') barrier -= 10
  if (morning === 'tirante') barrier -= 14
  if (concern === 'acne') barrier -= 10

  let cleansing = 40
  if (products === 'limpiador') cleansing = 68
  if (products === 'dostres') cleansing = 82
  if (products === 'rutina') cleansing = 92
  if (skinType === 'grasa' && products === 'ninguno') cleansing -= 8

  let protection = 30
  if (spf === 'aveces') protection = 55
  if (spf === 'diario') protection = 94
  if (concern === 'manchas') protection -= 12

  let antioxidants = 36
  if (products === 'dostres') antioxidants = 58
  if (products === 'rutina') antioxidants = 78
  if (concern === 'luminosidad' || concern === 'manchas') antioxidants -= 14
  if (goal === 'luminosidad' || goal === 'tono') antioxidants -= 8

  let nightRepair = 34
  if (products === 'dostres') nightRepair = 56
  if (products === 'rutina') nightRepair = 80
  if (age === '40s') nightRepair -= 10
  if (age === '50+') nightRepair -= 16
  if (concern === 'lineas' || goal === 'prevenir') nightRepair -= 8

  let habits = 60
  if (spf === 'diario') habits += 18
  if (spf === 'nunca') habits -= 22
  if (products === 'rutina') habits += 14
  if (products === 'ninguno') habits -= 14
  if (morning === 'comoda') habits += 8

  return [
    { key: 'hydration', label: 'Hidratación', score: clamp(hydration) },
    { key: 'barrier', label: 'Barrera cutánea', score: clamp(barrier) },
    { key: 'cleansing', label: 'Limpieza', score: clamp(cleansing) },
    { key: 'protection', label: 'Protección solar', score: clamp(protection) },
    { key: 'antioxidants', label: 'Antioxidantes', score: clamp(antioxidants) },
    { key: 'nightRepair', label: 'Reparación nocturna', score: clamp(nightRepair) },
    { key: 'habits', label: 'Hábitos diarios', score: clamp(habits) },
  ]
}

const focusFor = (answers: Answers): string[] => {
  const focus: string[] = []
  const { skinType, concern, morning, goal, spf, products } = answers

  if (skinType === 'sensible' || concern === 'rojez' || goal === 'calmar') {
    focus.push('Calmar y reforzar la barrera cutánea')
  } else {
    focus.push('Reparar la barrera cutánea')
  }

  if (
    skinType === 'seca' ||
    morning === 'tirante' ||
    concern === 'deshidratacion' ||
    goal === 'hidratacion'
  ) {
    focus.push('Recuperar y mantener la hidratación profunda')
  } else if (skinType === 'grasa' || morning === 'brillante') {
    focus.push('Equilibrar la hidratación sin saturar la piel')
  } else {
    focus.push('Mantener una hidratación constante')
  }

  if (
    concern === 'manchas' ||
    concern === 'luminosidad' ||
    goal === 'tono' ||
    goal === 'luminosidad'
  ) {
    focus.push('Incorporar antioxidantes que unifiquen el tono')
  } else if (concern === 'acne') {
    focus.push('Incorporar activos que regulen y purifiquen')
  } else {
    focus.push('Incorporar antioxidantes de forma diaria')
  }

  if (spf === 'nunca' || spf === 'aveces') {
    focus.push('Protegerse del sol todos los días, sin excepción')
  } else if (products === 'ninguno' || products === 'limpiador') {
    focus.push('Construir una rutina simple y sostenible')
  } else {
    focus.push('Sostener la protección diaria durante todo el año')
  }

  return focus
}

const headlineFor = (answers: Answers): string => {
  switch (answers.skinType) {
    case 'seca':
      return 'Tu piel muestra señales de sequedad y necesita recuperar reservas'
    case 'grasa':
      return 'Tu piel busca equilibrio, no menos cuidado'
    case 'sensible':
      return 'Tu piel es reactiva y necesita calma antes que intensidad'
    case 'mixta':
      return 'Tu piel tiene zonas con necesidades distintas'
    default:
      return 'Tu piel necesita orden y constancia antes que más productos'
  }
}

const explanationFor = (answers: Answers): string => {
  const parts: string[] = []

  if (answers.morning === 'tirante') {
    parts.push(
      'La sensación de tirantez al despertar suele indicar una barrera cutánea debilitada, que pierde agua durante la noche.',
    )
  } else if (answers.morning === 'brillante') {
    parts.push(
      'El brillo matinal muchas veces no es exceso de grasa, sino una respuesta de la piel a la falta de hidratación.',
    )
  } else if (answers.morning === 'variable') {
    parts.push(
      'Cuando la piel cambia de un día a otro, lo que suele faltar es constancia y no más activos.',
    )
  } else {
    parts.push(
      'Tu piel se encuentra en un punto estable: el objetivo ahora es cuidar y prevenir, con gestos simples y sostenidos.',
    )
  }

  if (answers.spf === 'nunca' || answers.spf === 'aveces') {
    parts.push(
      'Sumado a esto, la exposición solar sin protección constante acelera la aparición de manchas y líneas de expresión.',
    )
  }

  if (answers.products === 'ninguno' || answers.products === 'limpiador') {
    parts.push(
      'Con muy pocos pasos ya podés notar cambios visibles: el orden importa más que la cantidad.',
    )
  } else if (answers.products === 'rutina') {
    parts.push(
      'Ya tenés una rutina establecida, así que el foco está en ajustar qué usás y en qué momento.',
    )
  }

  return parts.join(' ')
}

export const buildDiagnosis = (answers: Answers): Diagnosis => {
  const all = dimensions(answers)
  const sorted = [...all].sort((a, b) => b.score - a.score)

  const strengths = sorted.filter((d) => d.score >= 68).slice(0, 3)
  const improvements = [...all]
    .sort((a, b) => a.score - b.score)
    .filter((d) => !strengths.some((s) => s.key === d.key))
    .slice(0, 4)

  const score = Math.round(all.reduce((sum, d) => sum + d.score, 0) / all.length)

  return {
    headline: headlineFor(answers),
    focus: focusFor(answers),
    explanation: explanationFor(answers),
    score,
    skinTypeLabel: skinTypeLabels[answers.skinType ?? ''] ?? 'Sin definir',
    strengths: strengths.length ? strengths : sorted.slice(0, 2),
    improvements: improvements.length ? improvements : sorted.slice(-3),
  }
}
